'use client';

import { c as _c } from "react/compiler-runtime";
import type { FileReviewDecision } from '@/lib/pr-v2/file-review-decisions';
import type { ReactNode } from 'react';
import type { PrV2GroupedCodeContent } from '../file-tree-v2/pr-v2-file-tree-utils';
import type { PrV2LeftGroupHeaderProps } from './pr-v2-left-group-header';
import { getPrV2GroupElementId } from '../file-tree-v2/pr-v2-file-tree-utils';
import { PrV2GroupSectionLayout } from './pr-v2-group-section-layout';
import { PrV2LeftGroupHeader } from './pr-v2-left-group-header';
import { cn } from '@/lib/utils';
type PrV2ToggleGroupReviewedArgs = {
  groupName: string;
  groupOrder: number;
  filenames: string[];
  isGroupReviewed: boolean;
};
export type PrV2GroupHeaderArgs = {
  group: PrV2GroupedCodeContent;
  previousGroup?: PrV2GroupedCodeContent;
  nextGroup?: PrV2GroupedCodeContent;
  sectionIndex: number;
  sectionCount: number;
  isGroupingPending: boolean;
  /** Every review decision on the group's files, including file-level ones. */
  decisions: readonly FileReviewDecision[];
  activeFilename: string | null;
  isGroupReviewed: boolean;
  onToggleGroupReviewed: (args: PrV2ToggleGroupReviewedArgs) => void;
  onNavigateGroup: (groupId: string, options: {
    alignHeaderToTop?: boolean;
  }) => void;
  onSelectFile: (filename: string) => void;
};
type PrV2GroupSectionProps = PrV2GroupHeaderArgs & {
  visibleGroupIndex: number;
  /** False when the group card in the chat column shows this group's header instead. */
  showGuide: boolean;
  children: ReactNode;
};

/** The guide beside a group and the group card in the chat column both render these props. */
export function getPrV2GroupHeaderProps({
  group,
  previousGroup,
  nextGroup,
  sectionIndex,
  sectionCount,
  isGroupingPending,
  decisions,
  activeFilename,
  isGroupReviewed,
  onToggleGroupReviewed,
  onNavigateGroup,
  onSelectFile
}: PrV2GroupHeaderArgs): PrV2LeftGroupHeaderProps {
  const groupId = getPrV2GroupElementId(group.groupName, group.groupOrder);
  const groupFilenames = group.files.map(file => file.filename);
  const onPreviousGroup = () => {
    if (!previousGroup) {
      return;
    }
    onNavigateGroup(getPrV2GroupElementId(previousGroup.groupName, previousGroup.groupOrder), {
      alignHeaderToTop: true
    });
  };
  const onNextGroup = () => {
    if (!nextGroup) {
      return;
    }
    onNavigateGroup(getPrV2GroupElementId(nextGroup.groupName, nextGroup.groupOrder), {
      alignHeaderToTop: true
    });
  };
  const onToggleReviewed = () => {
    onToggleGroupReviewed({
      groupName: group.groupName,
      groupOrder: group.groupOrder,
      filenames: groupFilenames,
      isGroupReviewed
    });
    if (!isGroupReviewed) {
      onNextGroup();
    }
  };
  const onToggleReviewedAction = groupFilenames.length > 0 ? onToggleReviewed : undefined;
  const onPreviousGroupAction = previousGroup ? onPreviousGroup : undefined;
  const onNextGroupAction = nextGroup ? onNextGroup : undefined;
  return {
    sectionIndex,
    sectionCount,
    groupId,
    groupName: group.groupName,
    groupNameReason: group.groupNameReason,
    files: group.files,
    decisions,
    isGroupingPending,
    activeFilename,
    isGroupReviewed,
    onSelectFile,
    onToggleGroupReviewed: onToggleReviewedAction,
    onNavigatePrevious: onPreviousGroupAction,
    onNavigateNext: onNextGroupAction
  };
}
export function PrV2GroupSection(t0) {
  const $ = _c(17);
  let children;
  let group;
  let sectionClassName;
  let showGuide;
  let t1;
  if ($[0] !== t0) {
    const {
      visibleGroupIndex,
      showGuide: t2,
      children: t3,
      ...headerArgs
    } = t0;
    showGuide = t2;
    children = t3;
    ({
      group
    } = headerArgs);
    const t4 = visibleGroupIndex > 0 && "pt-8";
    let t5;
    if ($[6] !== t4) {
      t5 = cn("space-y-3", t4);
      $[6] = t4;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    sectionClassName = t5;
    t1 = getPrV2GroupHeaderProps(headerArgs);
    $[0] = t0;
    $[1] = children;
    $[2] = group;
    $[3] = sectionClassName;
    $[4] = showGuide;
    $[5] = t1;
  } else {
    children = $[1];
    group = $[2];
    sectionClassName = $[3];
    showGuide = $[4];
    t1 = $[5];
  }
  const headerProps = t1;
  const t2 = `pr-v2-group-section-${group.groupOrder}`;
  let t3;
  if ($[8] !== headerProps || $[9] !== showGuide) {
    t3 = showGuide ? <PrV2LeftGroupHeader {...headerProps} /> : null;
    $[8] = headerProps;
    $[9] = showGuide;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== children || $[12] !== headerProps.groupId || $[13] !== sectionClassName || $[14] !== t2 || $[15] !== t3) {
    t4 = <PrV2GroupSectionLayout id={headerProps.groupId} data-testid={t2} className={sectionClassName} guide={t3}>{children}</PrV2GroupSectionLayout>;
    $[11] = children;
    $[12] = headerProps.groupId;
    $[13] = sectionClassName;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}